import { Folder } from '@/api/generatedApi';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { GET_FOLDER_QUERY_KEY } from './useGetFolder';

export const GET_FOLDER_HIERARCHY_QUERY_KEY = 'getFolderHierarchy';

export const useFolderHierarchy = ({ folderId }: { folderId?: string }) => {
  const { getTenantId } = useTenants();
  const tenantId = getTenantId();
  const queryClient = useQueryClient();

  const { data: hierarchy, isLoading } = useQuery({
    queryKey: [GET_FOLDER_HIERARCHY_QUERY_KEY, { folderId, tenantId }],
    queryFn: async () => {
      if (!folderId || !tenantId) return [];

      const hierarchy: Folder[] = [];
      let currentFolderId: string | undefined = folderId;

      while (currentFolderId) {
        const queryKey = [GET_FOLDER_QUERY_KEY, { folderId: currentFolderId, tenantId }];
        const response = await queryClient.ensureQueryData({
          queryKey,
          queryFn: () => api.folders.getFolder(tenantId, currentFolderId!),
        });
        const folder: Folder = response.data.folder;
        hierarchy.unshift(folder);
        currentFolderId = folder.parentFolderId;
      }

      return hierarchy;
    },
    enabled: !!tenantId,
    placeholderData: keepPreviousData,
  });

  return {
    hierarchy: tenantId ? (hierarchy ?? []) : [],
    folderPath: tenantId && hierarchy ? hierarchy.map(folder => folder.name).join('/') : '',
    isLoading,
  };
};
